import { useState, useEffect, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import {
  Card, Button, Input, Spinner, Alert, EmptyState,
  LineChart, DonutChart, BarChart, CHART_COLORS, formatNumber,
  type Series, type DonutDatum, type BarDatum,
} from '@/ui'
import { PRESETS, resolveRange, type PresetKey } from '@/lib/dateRange'
import styles from './InvitationViews.module.css'

interface ViewsData {
  scope: 'global' | 'invitation'
  invitationId: string | null
  invitationLabel: string | null
  from: string
  to: string
  totals: { views: number; uniqueVisitors: number }
  series: { date: string; views: number; uniqueVisitors: number }[]
  byDevice: { device: string; views: number }[]
  byReferer: { source: string; views: number }[]
}

export function InvitationViews() {
  const [sp, setSp] = useSearchParams()
  const { from, to, preset } = resolveRange(sp)
  const invitation = sp.get('invitation') ?? ''
  const [searchInput, setSearchInput] = useState(invitation)

  useEffect(() => {
    setSearchInput(invitation)
  }, [invitation])

  const qs = new URLSearchParams({ from, to })
  if (invitation) qs.set('invitation', invitation)
  const { data, error, isLoading } = useApiFetch<ViewsData>(
    `/api/admin/dashboard/invitation-views?${qs.toString()}`,
  )

  const setPreset = (key: PresetKey) => {
    const next = new URLSearchParams(sp)
    next.set('preset', key)
    next.delete('from')
    next.delete('to')
    setSp(next, { replace: true })
  }

  const applySearch = (e: FormEvent) => {
    e.preventDefault()
    const next = new URLSearchParams(sp)
    const v = searchInput.trim()
    if (v) next.set('invitation', v)
    else next.delete('invitation')
    setSp(next, { replace: true })
  }

  const clearDrilldown = () => {
    const next = new URLSearchParams(sp)
    next.delete('invitation')
    setSp(next, { replace: true })
    setSearchInput('')
  }

  // 일별 조회 추이 시리즈 구성
  const series: Series[] = data
    ? [
        {
          key: 'views',
          label: '조회수',
          color: CHART_COLORS.primary,
          data: data.series.map((s) => ({ date: s.date, value: s.views })),
        },
        {
          key: 'uniqueVisitors',
          label: '고유 방문자(근사)',
          color: CHART_COLORS.info,
          data: data.series.map((s) => ({ date: s.date, value: s.uniqueVisitors })),
        },
      ]
    : []

  // 디바이스 구성 도넛 차트 데이터
  const deviceData: DonutDatum[] = (data?.byDevice ?? []).map((d) => ({
    label: d.device,
    value: d.views,
  }))

  // 유입경로 바 차트 데이터
  const refererData: BarDatum[] = (data?.byReferer ?? []).map((r) => ({
    label: r.source,
    value: r.views,
  }))

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>조회 분석</h1>
        <div className={styles.controls}>
          {/* 기간 프리셋 버튼 */}
          <div className={styles.presets}>
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={preset === p.key ? 'primary' : 'ghost'}
                onClick={() => setPreset(p.key)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          {/* 청첩장 slug/ID 검색 */}
          <form className={styles.search} onSubmit={applySearch}>
            <Input
              size="sm"
              placeholder="청첩장 slug 또는 ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button size="sm" type="submit">검색</Button>
          </form>
        </div>
        {/* 드릴다운 표시 */}
        {data?.scope === 'invitation' && (
          <div className={styles.drilldown}>
            {data.invitationLabel
              ? <span>청첩장 <strong>{data.invitationLabel}</strong> 분석 중</span>
              : <span>청첩장을 찾을 수 없습니다</span>}
            <Button size="sm" variant="ghost" onClick={clearDrilldown}>✕ 전체로</Button>
          </div>
        )}
      </header>

      {error && <Alert variant="danger">{error}</Alert>}
      {isLoading && !data && <Spinner />}

      {data && (
        data.scope === 'invitation' && !data.invitationLabel ? (
          <EmptyState title="청첩장을 찾을 수 없습니다" />
        ) : data.totals.views === 0 ? (
          <EmptyState title="해당 기간 조회 데이터가 없습니다" />
        ) : (
          <>
            {/* KPI 카드 */}
            <div className={styles.kpis}>
              <Card compact>
                <p className={styles.kpiLabel}>총 조회수</p>
                <p className={styles.kpiValue}>{formatNumber(data.totals.views)}</p>
              </Card>
              <Card compact>
                <p className={styles.kpiLabel}>고유 방문자(근사)</p>
                <p className={styles.kpiValue}>{formatNumber(data.totals.uniqueVisitors)}</p>
              </Card>
            </div>
            {/* 일별 추이 라인 차트 */}
            <Card title="일별 조회 추이">
              <LineChart series={series} showTooltip showLegend />
            </Card>
            {/* 디바이스·유입경로 2열 그리드 */}
            <div className={styles.grid2}>
              <Card title="디바이스 구성">
                <DonutChart data={deviceData} showTooltip showLegend />
              </Card>
              <Card title="유입경로">
                <BarChart data={refererData} direction="horizontal" showTooltip />
              </Card>
            </div>
          </>
        )
      )}
    </div>
  )
}
